// src/TechSummit.js
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TechsubmitRegistrationPage_new from "./TechsubmitRegistrationPage_new";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './TechSummit.module.css';

// 1. Import the custom hook
import useEventData from '../hooks/useEventData'; 
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DropboxApply from './DropboxApply'
// --- ANIMATION VARIANTS (STATIC) ---
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15, 
        },
    },
};
const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            type: "spring",
            stiffness: 100,
        },
    },
};

const KEY_POINTS = [
    "🤝 Reconnect with peers and mentors","🚀 Showcase your tech brilliance and brand",
    "💡 Exchange ideas that shape the future","🍽 Enjoy Complimentary lunch and meaningful conversations"
];

const CONCLUSION_DELAY = 7.0; 

// --- HELPER COMPONENTS ---

const WordByWordAnimator = ({ text, totalDelay }) => {
    const words = text.split(" "); 
    const wordVariants = { 
        hidden: { opacity: 0, y: 5 },
        visible: {
            opacity: 1, y: 0,
            transition: { duration: 0.15, ease: "linear" },
        },
    };
    const containerVariants = { 
        visible: {
            transition: { staggerChildren: 0.05, delayChildren: totalDelay },
        },
    };

    return (
        <motion.span 
            className={styles.wordContainer}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {words.map((word, index) => (
                <motion.span 
                    key={index} 
                    variants={wordVariants}
                    className={styles.wordSpacer}
                >
                    {word}{" "} 
                </motion.span>
            ))}
        </motion.span>
    );
};

const AnimatedKeyPoints = () => {
    const initialTypingDelay = 1.8; 
    let cumulativeDelay = initialTypingDelay; 
    return (
        <motion.div className={styles.keyPointsContainer}>
            <ul className={styles.keyPointsList}>
                <AnimatePresence>
                    {KEY_POINTS.map((point) => {
                        const wordCount = point.split(" ").length; 
                        const lineTypingTime = wordCount * 0.05 + 0.5;
                        const currentLineDelay = cumulativeDelay;
                        cumulativeDelay += lineTypingTime + 0.3;

                        return (
                            <motion.li
                                key={point}
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }}
                                transition={{ delay: currentLineDelay, duration: 0.1 }}
                                className={styles.keyPointItem}
                            >
                                <WordByWordAnimator 
                                    text={point} 
                                    totalDelay={currentLineDelay}
                                />
                            </motion.li>
                        );
                    })}
                </AnimatePresence>
            </ul>
        </motion.div>
    );
};
const AnimatedPlaceholder = ({ text, className, delay = 0.5 }) => {
    return (
        <motion.span
            className={className} 
            initial={{ y: 0 }}
            // Fix: Use only two keyframes for 'spring' type animation.
            // Animates from 0px to -5px (up), and 'reverse' handles the motion back to 0px.
            animate={{ y: [0, -5] }} 
            transition={{ 
                repeat: Infinity,
                repeatType: "reverse", 
                duration: 2.0, // Adjusted duration for a slower, more noticeable "waiting" bounce
                delay: delay,
                type: "spring", // Keeps the desired spring/bounce physics
                stiffness: 150,
                damping: 10, // A lower damping value creates a more pronounced, gentle bounce
                mass: 1
            }}
            style={{ display: 'inline-block' }} // Essential for y-translation to work
        >
            {text}
        </motion.span>
    );
};

const CountdownTimer = ({ eventDateString }) => {
    
    // 1. Call Hooks unconditionally at the top 
    const [timeLeft, setTimeLeft] = useState({});

    const isValidDate = eventDateString && !isNaN(new Date(eventDateString));

    const EVENT_DATE = new Date(eventDateString);
    
    const calculateTimeLeft = () => {
        const difference = +EVENT_DATE - +new Date();
        let calculatedTimeLeft = {};
    
        if (difference > 0) {
            calculatedTimeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        }
        return calculatedTimeLeft;
    };

    // 2. Use conditional logic INSIDE useEffect (or return early from useEffect)
    useEffect(() => {
        // Stop the timer logic if the date isn't valid
        if (!isValidDate) return; 

        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [eventDateString, isValidDate]); 

    // 3. Conditional Return based on validation
    if (!isValidDate) {
        return (
            <motion.div 
                className={styles.countdownContainer}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
            >
                <p className={styles.stayTunedText}>
                    Date and Countdown **TBA** - Stay Tuned!
                </p>
            </motion.div>
        );
    }

    const timerComponents = Object.keys(timeLeft).map((interval) => {
        if (!timeLeft[interval] && timeLeft[interval] !== 0) {
            return null;
        }
        return (
            <motion.div 
                key={interval} 
                className={styles.countdownItem}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 150 }}
            >
                <span className={styles.countdownValue}>{String(timeLeft[interval]).padStart(2, '0')}</span>
                <span className={styles.countdownLabel}>{interval}</span>
            </motion.div>
        );
    });

    return (
        <div className={styles.countdownContainer}>
            {timerComponents.length ? (
                timerComponents
            ) : (
                <motion.span 
                    initial={{ scale: 0.8 }} 
                    animate={{ scale: 1 }} 
                    className={styles.eventLive}
                >
                    The Event is LIVE! 🚀
                </motion.span>
            )}
        </div>
    );
};
const Marquee = ({ children, duration = 20 }) => {
    
    // CRITICAL: Double the children so the animation loop is seamless.
    // When the first set scrolls off-screen, the second set takes its place, 
    // and the animation jumps back to the start position.
    const doubledChildren = [...children, ...children];

    // Define the animation from 0% to -50% (the width of the single set of partners)
    const marqueeVariants = {
        animate: {
            // Move from the start position (0) to the position where the first set of partners
            // is exactly off-screen (the start of the second set).
            x: ['0%', '-50%'], 
            transition: {
                x: {
                    duration: duration, 
                    ease: 'linear',
                    repeat: Infinity, // CRITICAL: Loop forever
                    repeatType: 'loop',
                },
            },
        },
    };

    // The inner content MUST start the animation immediately.
    return (
        <motion.div
            className={styles.marqueeInner} 
            variants={marqueeVariants}
            initial="animate" // CRITICAL: Start the animation immediately on mount
            animate="animate" // Use the same target to trigger the animation loop
        >
            {doubledChildren.map((item, index) => (
                <div 
                    // Use a combined key for uniqueness since we doubled the array
                    key={`${item.partner_id}-${index}`} 
                    className={styles.marqueeItem}
                >
                    <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/api/partner/logo/${item.partner_id}`}
                        alt={item.name}
                        className={styles.partnerLogo}
                        loading="lazy"
                    />
                </div>
            ))}
        </motion.div>
    );
};
// ==========================================================
// 🚀 Main Component
// ==========================================================
const TechSummit = () => {
    const [openDropbox, setOpenDropbox] = useState(false);
    const [openRegistration, setOpenRegistration] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('dropbox') === 'resume') {
        setOpenDropbox(true);
        }
    }, []);
    const handleOpenRegistration = (e) => {
    e.preventDefault();
    setOpenRegistration(true);
    };

    const handleCloseRegistration = () => {
    setOpenRegistration(false);
    };

    // 2. Fetch data using the hook
    const { data, loading, error } = useEventData();

    // --- Loading and Error States ---
    if (loading) {
        return <div className={styles.loadingContainer}>Loading Event Details...</div>;
    }

    if (error || !data) {
        return <div className={styles.errorContainer}>Error: {error || "No event data available."}</div>;
    }
    
    const handleCloseDropbox = () => {
        setOpenDropbox(false);
        const url = new URL(window.location);
        url.searchParams.delete('dropbox');
        window.history.replaceState(null, '', url.toString());
    };

    
    const goToDropBox = () => {
        const url = new URL(window.location);
        url.searchParams.set('dropbox', "resume");
        window.history.replaceState(null, '', url.toString());
        setOpenDropbox(true);
    };
   
    // 3. Destructure the fetched data
    const { eventDetails, speakers, agenda, highlights, sponsorshipPackages,partnerships,contact,sponsorshipBenefits,guestOfHonour } = data;
    // ⭐ NEW: Conditional values for Date and Venue
    // ⭐ Conditional values for Date and Venue (for display)
    const displayDate = eventDetails.date && eventDetails.date.trim() !== '' 
        ? eventDetails.date + ' - ' + eventDetails.time
        : null; // Set to null to trigger the animated placeholder

    const displayVenue = eventDetails.venue && eventDetails.venue.trim() !== ''
        ? eventDetails.venue 
        : null; // Set to null to trigger the animated placeholder
    // Pass the original date string to the timer (it handles null/invalid)
    const eventDateString = eventDetails.date; 
    return (
        <>
           
        <div className={styles.pageContainer}> 
            {/* Add this icon */}
             <div className={styles.dropboxIcon}>
                        <IconButton onClick={goToDropBox} style={{ color: "white" }}>
                            <CloudUploadIcon fontSize="large" />
                        </IconButton>
            </div>
            {/* -------------------------------------------------- */}
            {/* 🌟 Hero Section */}
            {/* -------------------------------------------------- */}
            <motion.section 
                className={styles.hero}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className={styles.heroContent}>
                    <img
                        src={require('../UC transparent logo.png')}
                        alt="Udyog Connect Logo"
                        className={styles.heroLogo}
                    />
                    <motion.div className={styles.heroHeader}>
                        <span className={styles.spark}>✨</span>
                        <a
                            href="https://www.udyogconnect.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#fff", textDecoration: "none", fontWeight: "bold" }}
                        >
                            Udyog Connect Presents
                        </a>
                        <span className={styles.spark}>✨</span>
                    </motion.div>

                    <motion.p 
                        className="text-lg uppercase tracking-widest mb-3 opacity-80"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                    >
                        {displayDate ? (
                            displayDate
                        ) : (
                            <AnimatedPlaceholder 
                                text="Stay Tuned for the Date & Time! 📅" 
                                className={styles.placeholderDate}
                                delay={0.5}
                            />
                        )}
                    </motion.p>
                    <motion.h1
                        className={styles.eventName} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 50, delay: 0.2 }}>
                        {eventDetails.name}
                    </motion.h1>
                    <motion.p
                        className={styles.tagline} // ***Use a new, dedicated CSS class***
                        initial={{ y: 20, opacity: 0 }} // ***New animation: slide up and fade in***
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 100, delay: 0.6 }}>
                        {eventDetails.subtitle}
                    </motion.p>
                    <motion.p 
                        className={styles.venueDetails}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.7 }}
                    >
                        {displayVenue ? (
                            displayVenue
                        ) : (
                            <AnimatedPlaceholder 
                                text="Stay Tuned for the Venue! 📍" 
                                className={styles.placeholderVenue}
                                delay={1.0} // Staggered delay for the venue
                            />
                        )}
                    </motion.p>                  
                    <AnimatedKeyPoints />
                    <CountdownTimer eventDateString={eventDetails.date} /> 
                    
                    {/* <motion.button
                        onClick={handleOpenRegistration}
                        className={styles.registrationButton}
                        whileHover={{ scale: 1.05, rotate: 1 }}
                        whileTap={{ scale: 0.95 }}
                        >
                        Register Now! 🎟️
                    </motion.button> */}

                    {eventDetails?.freeentrytill && (
                        <>
                        <p style={{ marginTop: "16px" }}>
                        <a
                            style={{ color: "#ff4d4d", fontWeight: "bold", textDecoration: "none" }}
                        >
                           {eventDetails.freeentrytill}
                        </a>
                        </p>
                        </>
                        
                    )}
                    <p style={{ marginTop: "16px" }}>
                    👉{" "}
                    <a
                        href="https://www.udyogconnect.org/"
                        style={{ color: "#0073e6", fontWeight: "bold", textDecoration: "none" }}
                    >
                        Explore Udyog Connect Community
                    </a>
                    </p>


                </div>
            </motion.section>
            {guestOfHonour && guestOfHonour[0].name && (
                <motion.section 
                    className={styles.section} 
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                >
                    <h2 className={styles.sectionTitle}>
                        Guest of Honour
                    </h2>
                    
                    <motion.div 
                        className={styles.guestOfHonourCard}
                        variants={itemVariants} // Use the standard item animation
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                    >
                        <img
                            // Assuming the image URL structure is similar to speakers, 
                            // using a specific ID or a known file path.
                            // Replace 'guest-of-honour-image-id' with the correct ID if needed
                            src={`${process.env.REACT_APP_API_BASE_URL}/api/guest/image/${guestOfHonour[0].speaker_id}`}
                            alt={`guest of ${guestOfHonour[0].name}`}
                            className={styles.honourImage}
                            width="250" // Adjust size as needed
                            height="250" // Adjust size as needed
                            loading="lazy"
                        />
                        <div className={styles.honourDetails}>
                            <p className={styles.honourRole}>✨ {guestOfHonour[0].title|| 'Distinguished Guest'} ✨</p>
                            <h3 className={styles.honourName}>{guestOfHonour[0].name}</h3>
                            <p className={styles.honourDescription}>{guestOfHonour[0].description}</p>
                        </div>
                    </motion.div>
                </motion.section>
            )}
            {/* -------------------------------------------------- */}
            {/* 🎙️ Speakers List Section */}
            {/* -------------------------------------------------- */}
            <section // <-- 1. CRITICAL: Use a regular HTML 'section' tag here
                className={styles.section}
                // REMOVED: initial, whileInView, viewport props
            >
                <h2 className={styles.sectionTitle}>
                    Meet Our Visionary Speakers {/* <-- Title always visible */}
                </h2>
                
                <motion.div 
                    className={styles.speakersGrid}
                    variants={containerVariants}
                    // 2. CRITICAL: MOVED animation logic here:
                    initial="hidden" 
                    whileInView="visible" 
                    viewport={{ once: true, amount: 0.3 }}
                >
                    {speakers.map((speaker) => (
                        <motion.div 
                            // ... individual speaker card code ...
                            variants={itemVariants}
                        >
                            <img
                src={`${process.env.REACT_APP_API_BASE_URL}/api/speaker/image/${speaker.speaker_id}`}
                alt={`Portrait of ${speaker.name}`}
                className={styles.speakerImage}
                width="150"
                height="150"
                loading="lazy"
                />
                <h3 className={styles.speakerName}>{speaker.name}</h3>
                <p className={styles.speakerTitle}>{speaker.title}</p>
                        </motion.div>
                    ))}
                </motion.div>
            </section>
            {/* -------------------------------------------------- */}
            {/* 📅 Agenda Section */}
            {/* -------------------------------------------------- */}
            <motion.section 
                className={`${styles.section} ${styles.agenda}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                <h2 className={styles.sectionTitle}>
                    Full Event Agenda
                </h2>
                
                <motion.div variants={containerVariants}>
                    {agenda.map((item, index) => {
                        const isBreak = item.topic.includes('Break');
                        
                        return (
                            <motion.div
                                key={item.time}
                                className={`${styles.agendaItem} ${isBreak ? styles.breakItem : ''}`}
                                variants={itemVariants}
                            >
                                <div className={styles.agendaTime}>
                                    {item.time}
                                </div>
                                <div className={styles.agendaContent}>
                                    <h3 className={styles.agendaTopic}>
                                        {item.topic}
                                    </h3>
                                    {item.speaker && (
                                        <p className={styles.agendaSpeaker}>
                                            Speaker: <strong>{item.speaker}</strong>
                                        </p>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </motion.section>

            {/* -------------------------------------------------- */}
            {/* 💡 Summit Highlights */}
            {/* -------------------------------------------------- */}
            <motion.section 
                className={`${styles.section} ${styles.highlightsSection}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <h2 className={styles.sectionTitle}>
                    Summit Highlights
                </h2>

                <motion.div className={styles.pillarGrid} variants={containerVariants}>
                    {highlights.pillars.map((pillar, index) => (
                        <motion.div 
                            key={pillar.title} 
                            className={styles.pillarCard}
                            variants={itemVariants}
                        >
                            <h3>{pillar.title}</h3>
                            <p>{pillar.detail}</p>
                        </motion.div>
                    ))}
                </motion.div>

                <motion.ul 
                    className={styles.featureList} 
                    variants={containerVariants}
                >
                    {highlights.features.map((feature, index) => (
                        <motion.li key={index} variants={itemVariants}>
                            {feature}
                        </motion.li>
                    ))}
                </motion.ul>
            </motion.section>
            
            {/* -------------------------------------------------- */}
            {/* 🤝 Partnerships Section */}
            {/* -------------------------------------------------- */}
        
            <section 
                className={`${styles.section} ${styles.partnersSection}`}
                // REMOVED: initial/whileInView/viewport, as the inner Marquee component handles flow
            >
                <h2 className={styles.sectionTitle}>Meet Our Industry-Leading Partners</h2>
                
                {/* Marquee Wrapper: Uses the overflow: hidden CSS */}
                <div 
                    className={styles.marqueeContainer}
                >
                    {/* Use the Marquee component for the looping animation */}
                    {partnerships && partnerships.length > 0 ? (
                        <Marquee duration={20} children={partnerships} /> 
                    ) : (
                         <p>Loading partners...</p>
                    )}
                </div>

            </section>
                    
            {/* -------------------------------------------------- */}
            {/* 🤝 Sponsorship Opportunities */}
            {/* -------------------------------------------------- */}
            <motion.section 
                className={`${styles.section} ${styles.sponsorshipSection}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <h2 className={`${styles.sectionTitle} ${styles.sponsorshipTitle}`}>
                    Sponsorship Opportunities
                </h2>
                
                <motion.p style={{ textAlign: 'center', color: '#ccc', marginBottom: '40px' }}>
                    {sponsorshipPackages.intro}
                </motion.p>
                <motion.div className={styles.packageGrid} variants={containerVariants}>
                    {sponsorshipPackages.tiers.map((pkg, index) => {
                        const tierClass = styles[pkg.level.toLowerCase()]; 

                        return (
                            <motion.div 
                                key={pkg.level}
                                className={`${styles.sponsorCard} ${tierClass}`}
                                variants={itemVariants}
                                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }} 
                            >
                                <h3>{pkg.level} Sponsor</h3>
                                <p>{pkg.price}</p>
                                <span style={{color: '#9ca3af', marginBottom: '15px'}}>{pkg.description}</span>
                                <ul>
                                    {pkg.features.map((feature, i) => (
                                        <li key={i}>{feature}</li>
                                    ))}
                                </ul>
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* --- Exhibition Booths Callout --- */}
                <motion.div style={{ textAlign: 'center', paddingTop: '20px', borderTop: '1px solid #4f46e555' }} variants={itemVariants}>
                    <h3 style={{color: '#fff', fontSize: '1.5rem', marginBottom: '10px'}}>Exhibition Booths Available</h3>
                    <p style={{color: '#ccc', margin: '0 0 5px 0'}}>
                        {sponsorshipPackages.booths[0].name}: {sponsorshipPackages.booths[0].price} | 
                        {sponsorshipPackages.booths[1].name}: {sponsorshipPackages.booths[1].price}
                    </p>
                    <p style={{color: '#9ca3af', fontSize: '0.9rem'}}>All booths include table, chairs, power access, and event Wi-Fi.</p>
                </motion.div>

            </motion.section>

            {/* -------------------------------------------------- */}
            {/* 📞 Why Sponsor / Next Steps */}
            {/* -------------------------------------------------- */}
            <motion.section 
                className={styles.section}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
            >
                <motion.div className={styles.whySponsorContainer} variants={containerVariants}>
                    
                    <motion.div className={styles.benefitsCard} variants={itemVariants}>
                        
                        {/* Use the title fetched from the database/BFF */}
                        <h3>{sponsorshipBenefits.title || "Why Sponsor the Summit?"}</h3>
                        
                        {/* 💥 CRITICAL CHANGE: Use benefitsListTable to apply the new table-like CSS */}
                        <ul className={styles.benefitsListTable}> 
                            {sponsorshipBenefits.benefits.map((benefit, index) => (
                                <li 
                                    key={index}
                                    // Use dangerouslySetInnerHTML to correctly process **bolding**
                                    dangerouslySetInnerHTML={{ 
                                        __html: benefit.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    }}
                                >
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                    
                    <motion.div className={styles.contactCard} variants={itemVariants}>
                        <h3>Ready to Partner?</h3>
                        <div className={styles.contactInfo}>
                            <p>For sponsorship inquiries, please contact our team:</p>
                            <a href={`mailto:${sponsorshipPackages.contactEmail}`} className={styles.contactLink}>
                                {sponsorshipPackages.contactEmail}
                            </a>
                        </div>
                        <p style={{fontSize: '0.8rem', opacity: 0.7}}>Udyog Connect confidential document</p>
                    </motion.div>

                </motion.div>
            </motion.section>
            <section className={styles.footerSection}>
                <div className={styles.footerContent}>
                    <h3>Contact Us</h3>
                    <div className={styles.contactDetails}>
                        {contact.email && <p>📧 Email: <a href={`mailto:${contact.email}`}>{contact.email}</a></p>}
                        {contact.phone_number && <p>📞 Phone: <a href={`tel:${contact.phone_number}`}>{contact.phone_number}</a></p>}
                    </div>
                    
                    <div className={styles.ownershipDetails}>
                        {contact.site_owner_by && <p>Site Owner: **{contact.site_owner_by}**</p>}
                        {contact.website_created_by && <p>Website Created by: {contact.website_created_by}</p>}
                    </div>

                    {contact.rights_reserved_by && (
                        <p className={styles.copyright}>
                            &copy; {new Date().getFullYear()} {contact.rights_reserved_by}. All Rights Reserved.
                        </p>
                    )}
                </div>
            </section>
        </div>
        <Dialog
            slotProps={{
                backdrop: {
                sx: { backgroundColor: "rgba(0,0,0,0.4)" }, // ✅ replaces BackdropProps
                },
                paper: {
                sx: {
                    borderRadius: 3,
                    p: 2,
                    backgroundColor: "#fff", // ✅ replaces PaperProps
                    color: "#000",
                },
                },
            }}
            open={openRegistration}
            onClose={handleCloseRegistration}
            fullWidth
            maxWidth="md"
            PaperProps={{
                style: { borderRadius: 20, padding: "10px", backgroundColor: "#0f172a" }
            }}
            >
            <DialogContent style={{ position: "relative", padding: 0,backgroundColor: "#fff", }}>
                <IconButton
                onClick={handleCloseRegistration}
                style={{ position: "absolute", top: 10, right: 10, color: "white" }}
                >
                <CloseIcon />
                </IconButton>
                <div style={{ marginTop: 40 }}>
                <TechsubmitRegistrationPage_new
                    sponsorshipPackages={sponsorshipPackages}
                    sponsorshipBenefits={sponsorshipBenefits}
                    />
                </div>
            </DialogContent>
        </Dialog>
        {/* ⭐ NEW: Dropbox Apply Dialog */}
 
         <Dialog
            slotProps={{
                backdrop: {
                sx: { backgroundColor: "rgba(0,0,0,0.4)" }, // ✅ replaces BackdropProps
                },
                paper: {
                sx: {
                    borderRadius: 3,
                    p: 2,
                    backgroundColor: "#fff", // ✅ replaces PaperProps
                    color: "#000",
                },
                },
            }}
            open={openDropbox}
            onClose={handleCloseDropbox}
            fullWidth
            maxWidth="md"
            PaperProps={{
                style: { borderRadius: 20, padding: "10px", backgroundColor: "#0f172a" }
            }}
            >
            <DialogContent style={{ position: "relative", padding: 0,backgroundColor: "#fff", }}>
                <IconButton
                onClick={handleCloseDropbox}
                style={{ position: "absolute", top: 10, right: 10, color: "white" }}
                >
                <CloseIcon />
                </IconButton>
                <div style={{ marginTop: 40 }}>
                <DropboxApply />
                </div>
            </DialogContent>
        </Dialog>    
 
 </>

        
    );
};

export default TechSummit;